import { FC, useMemo, useRef } from "react";
import { FieldValues } from "react-hook-form";
import {
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiTable,
  MuiTypography,
} from "../../../../../../components";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { createProgramSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";

import { ViewActivityResponse } from "../../../../../../api/services/programTemplate/models";
import { UnitTypesKey } from "../../../../../../helpers/constants/siteCoreKeys";

interface ViewActivityDefaultTabProps extends FieldValues {
  activityData: ViewActivityResponse;
}

export const ViewActivityDefault: FC<ViewActivityDefaultTabProps> = ({
  activityData,
}) => {
  const gridSize = { md: 4, sm: 6, xs: 12 };
  const { fetchLabel, fetchValueByKeyOfDropdown, commonLabels } = useSiteCore();
  const tableRef = useRef<{ resetPagination: () => void }>(null);

  const getUnitText = () => {
    if (activityData?.units === UnitTypesKey.Minutes) {
      return createProgramSiteCoreKeys.minutes;
    } else if (activityData?.units === UnitTypesKey.Count) {
      return createProgramSiteCoreKeys.quantity;
    } else if (activityData?.units === UnitTypesKey.Days) {
      return createProgramSiteCoreKeys.numberofDays;
    } else {
      return createProgramSiteCoreKeys.halfDay;
    }
  };

  const measurementKey =
    activityData?.units === UnitTypesKey.Count
      ? createProgramSiteCoreKeys.quantity
      : createProgramSiteCoreKeys.numberofDays;

  const measurementLabel =
    activityData?.units === UnitTypesKey.Minutes
      ? fetchLabel(
          createProgramSiteCoreKeys.defaultActivityKey,
          createProgramSiteCoreKeys.minutes,
        )
      : fetchLabel(
          createProgramSiteCoreKeys.defaultActivityKey,
          measurementKey,
        );

  const columns = [
    {
      id: "countryTitle",
      label: fetchLabel(
        createProgramSiteCoreKeys.defaultActivityKey,
        createProgramSiteCoreKeys.country,
      ),
      isSortVisible: true,
      className: "cell-country",
    },
    // ...(activityData?.units !== UnitTypesKey.HalfDay ? [
    {
      id: "measurement",
      label: measurementLabel,
      isSortVisible: true,
      className: "cell-measurement",
    },
    // ] : []),
  ];

  const defaultValueExceptions = useMemo(() => {
    return Array.isArray(activityData?.defaultValueExceptions)
      ? activityData.defaultValueExceptions.map((item) => ({
          countryTitle: item.countryTitle,
          measurement:
            item.units === UnitTypesKey.Minutes
              ? Number(
                  fetchValueByKeyOfDropdown(
                    createProgramSiteCoreKeys.defaultActivityKey,
                    createProgramSiteCoreKeys.minutesList,
                    item.measurement,
                  ),
                )
              : Number(item.measurement),
        }))
      : [];
  }, [activityData]);

  const getMeasurementValue = () => {
    if (!activityData?.units) {
      return "";
    }
    if (activityData.units === UnitTypesKey.Minutes) {
      return fetchValueByKeyOfDropdown(
        createProgramSiteCoreKeys.defaultActivityKey,
        createProgramSiteCoreKeys.minutesList,
        activityData.measurement,
      );
    }
    return activityData.measurement;
  };
  return (
    <MuiBox>
      <MuiGrid
        container
        data-testid="activity-tab-default"
        className="activity-tab-general"
      >
        <MuiGrid size={gridSize}>
          <MuiDoubleLabel
            label={fetchLabel(
              createProgramSiteCoreKeys.defaultActivityKey,
              createProgramSiteCoreKeys.units,
            )}
            value={fetchValueByKeyOfDropdown(
              createProgramSiteCoreKeys.defaultActivityKey,
              createProgramSiteCoreKeys.unitLists,
              activityData?.units ?? "",
            )}
          />
        </MuiGrid>
        <MuiGrid size={gridSize}>
          <MuiDoubleLabel
            data-testid="measurement"
            label={fetchLabel(
              createProgramSiteCoreKeys.defaultActivityKey,
              getUnitText(),
            )}
            value={getMeasurementValue()}
          />
        </MuiGrid>
      </MuiGrid>
      {defaultValueExceptions.length > 0 && (
        <>
          <MuiBox>
            <MuiTypography
              variant="h2Bold"
              className="select-applicable"
              component="h2"
            >
              {fetchLabel(
                createProgramSiteCoreKeys.defaultActivityKey,
                createProgramSiteCoreKeys.exceptions,
              )}
            </MuiTypography>
          </MuiBox>

          <MuiGrid container className="activity-tab-general">
            <MuiGrid size={{ xs: 12 }} className="header-right">
              <MuiTable
                ref={tableRef}
                pageId={""}
                columns={columns}
                tableResults={defaultValueExceptions ?? []}
                noDataInApi={defaultValueExceptions.length === 0}
                noResultMessage={commonLabels.noResult}
                tableHeaderForm={[]}
                footerColSpan={1}
                priorityResults={[]}
              />
            </MuiGrid>
          </MuiGrid>
        </>
      )}
    </MuiBox>
  );
};
